import { useState } from 'react';
import styled from 'styled-components';

import { colors } from 'constants/colors';
import { QRCodeCanvas } from 'qrcode.react';

import { DetailLink } from 'components/DetailsView';
import { TotemCheckbox } from 'components/TotemCheckbox';
import { getColorsFromState } from 'pages/Locations/components/StatusTag';
import { QRCodeModal } from '../../../components/QRCodeModal';

import { GetLocationsByWarehouseColumnIdQuery } from 'data/__generated__';

export const LocationTrelloItem = ({
    location,
    isSelected,
    toggleSelection,
}: {
    location: GetLocationsByWarehouseColumnIdQuery['locationsByWarehouseColumnId'][number];
    isSelected: boolean;
    toggleSelection: () => void;
}) => {
    const { state, datamatrix, locationType, productId, product, index } = location;
    const [showQRCode, setShowQRCode] = useState(false);

    const { backgroundColor, labelColor } = getColorsFromState(state);
    return (
        <ItemContent>
            <ItemHeader onClick={toggleSelection} $backgroundColor={backgroundColor} $labelColor={labelColor}>
                <HeaderTitle>
                    {datamatrix}
                    {locationType ? (
                        <LocationTypeBadge $isPicking={locationType === 'Picking'}>
                            {locationType === 'Picking' ? 'P' : 'R'}
                        </LocationTypeBadge>
                    ) : null}
                </HeaderTitle>
                <TotemCheckbox checked={isSelected} onChange={toggleSelection} />
            </ItemHeader>
            <ItemBody>
                <Details>
                    <Detail>
                        <DetailLabel>Index de préparation</DetailLabel>
                        <DetailValue>{index}</DetailValue>
                    </Detail>
                    <Detail>
                        <DetailLabel>Produit</DetailLabel>
                        {productId ? (
                            <DetailLink
                                path="product"
                                name={product?.name || 'Produit inconnu'}
                                value={productId}
                                fontSize="14px"
                            />
                        ) : (
                            <AccentValue>Emplacement libre</AccentValue>
                        )}
                    </Detail>
                    {datamatrix && (
                        <Detail>
                            <DetailLabel>QR Code</DetailLabel>
                            <QRCodeButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowQRCode(true);
                                }}
                            >
                                <QRCodeCanvas value={datamatrix} size={30} />
                            </QRCodeButton>
                            <QRCodeModal
                                isOpen={showQRCode}
                                onClose={() => setShowQRCode(false)}
                                value={datamatrix}
                                showValue
                                valueLabel="Datamatrix"
                            />
                        </Detail>
                    )}
                </Details>
            </ItemBody>
        </ItemContent>
    );
};

const ItemContent = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${colors.pantinGrey};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    cursor: default;
`;

const ItemHeader = styled.div<{ $backgroundColor: string; $labelColor: string }>`
    border-bottom: 1px solid ${colors.pantinGrey};
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0 0;
    color: ${({ $labelColor }) => $labelColor};
    padding: 2px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
`;

const HeaderTitle = styled.div`
    display: flex;
    margin-left: 5px;
    font-weight: 800;
    align-items: center;
    gap: 6px;
`;

const LocationTypeBadge = styled.span<{ $isPicking: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${({ $isPicking }) => ($isPicking ? colors.orange : colors.blue)};
    color: white;
    font-size: 11px;
    font-weight: 700;
`;

const ItemBody = styled.div`
    padding: 5px 10px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`;

const Detail = styled.div`
    display: flex;
    flex: 1;
    white-space: nowrap;
    justify-content: space-between;
    color: 'inherit';
    align-items: center;

    &:not(:first-child) {
        margin-top: 5px;
    }
`;

const DetailLabel = styled.span`
    font-size: 14px;
    white-space: nowrap;
    margin-right: 10px;
`;

const DetailValue = styled.span`
    font-size: 14px;
    font-weight: 800;
    margin-left: 5px;
    text-align: right;
`;

const AccentValue = styled.span`
    font-size: 14px;
    font-weight: 800;
    color: ${colors.blue};
`;

const QRCodeButton = styled.button`
    padding: 0;
    border: none;
    background-color: white;
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        opacity: 0.8;
    }
`;
